import { useState, useEffect, useCallback } from 'react'
import type { PrivateMessage } from '@/types/message'

interface PrivateConversationCache {
  [userId: string]: {
    messages: PrivateMessage[]
    lastUpdated: number
    hasMoreHistory: boolean
  }
}

interface UsePrivateChatCacheProps {
  tableId: string | null
  enabled?: boolean
  maxCacheAge?: number // em milissegundos, padrão 5 minutos
}

export function usePrivateChatCache({
  tableId,
  enabled = true,
  maxCacheAge = 5 * 60 * 1000 // 5 minutos
}: UsePrivateChatCacheProps) {
  const [cache, setCache] = useState<PrivateConversationCache>({})

  // Chave para localStorage específica da mesa
  const getCacheKey = useCallback(() => {
    return tableId ? `private_chat_cache_${tableId}` : null
  }, [tableId])

  // Carregar cache do localStorage na inicialização
  useEffect(() => {
    if (!enabled || !tableId) return

    const cacheKey = getCacheKey()
    if (!cacheKey) return

    try {
      const cached = localStorage.getItem(cacheKey)
      if (cached) {
        const parsedCache: PrivateConversationCache = JSON.parse(cached)
        
        // Filtrar conversas expiradas
        const now = Date.now()
        const validCache: PrivateConversationCache = {}
        
        Object.entries(parsedCache).forEach(([userId, data]) => {
          if (now - data.lastUpdated < maxCacheAge) {
            validCache[userId] = data
          }
        })
        
        setCache(validCache)
        
        // Salvar cache limpo de volta
        if (Object.keys(validCache).length !== Object.keys(parsedCache).length) {
          localStorage.setItem(cacheKey, JSON.stringify(validCache))
        }
      }
    } catch (error) {
      console.error('Erro ao carregar cache de conversas privadas:', error)
    }
  }, [tableId, enabled, maxCacheAge, getCacheKey])

  // Salvar cache no localStorage quando houver mudanças
  const saveCache = useCallback((newCache: PrivateConversationCache) => {
    if (!enabled || !tableId) return

    const cacheKey = getCacheKey()
    if (!cacheKey) return

    try {
      localStorage.setItem(cacheKey, JSON.stringify(newCache))
    } catch (error) {
      console.error('Erro ao salvar cache de conversas privadas:', error)
    }
  }, [enabled, tableId, getCacheKey])

  // Obter mensagens de um usuário do cache
  const getCachedMessages = useCallback((userId: string): PrivateMessage[] | null => {
    if (!enabled) return null

    const conversation = cache[userId]
    if (!conversation) return null

    // Verificar se o cache ainda é válido
    const now = Date.now()
    if (now - conversation.lastUpdated > maxCacheAge) {
      return null
    }

    return conversation.messages
  }, [cache, enabled, maxCacheAge])

  // Escritas usam updater funcional (setCache(prev => ...)): as versoes
  // antigas fechavam sobre o state `cache` e duas mensagens no mesmo ciclo
  // faziam a segunda gravar por cima de um cache stale, perdendo a primeira.
  // Bonus: sem `cache` nos deps, as identidades ficam estaveis e o listener
  // de WS do usePrivateChat para de ser removido/readicionado a cada escrita.
  // O saveCache dentro do updater e idempotente (mesmo valor serializado).

  // Armazenar mensagens de um usuário no cache
  const setCachedMessages = useCallback((userId: string, messages: PrivateMessage[], hasMoreHistory = false) => {
    if (!enabled) return

    setCache(prev => {
      const newCache = {
        ...prev,
        [userId]: {
          messages,
          lastUpdated: Date.now(),
          hasMoreHistory
        }
      }
      saveCache(newCache)
      return newCache
    })
  }, [enabled, saveCache])

  // Adicionar nova mensagem ao cache existente
  const addMessageToCache = useCallback((userId: string, message: PrivateMessage) => {
    if (!enabled) return

    setCache(prev => {
      const existing = prev[userId]
      const baseMessages = existing?.messages ?? []

      // Verificar se a mensagem já existe (evitar duplicatas)
      if (baseMessages.some(msg => msg.id === message.id)) return prev

      // Adicionar nova mensagem (ordenar por timestamp)
      const updatedMessages = [...baseMessages, message].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )

      const newCache = {
        ...prev,
        [userId]: {
          messages: updatedMessages,
          lastUpdated: Date.now(),
          hasMoreHistory: existing?.hasMoreHistory ?? false
        }
      }
      saveCache(newCache)
      return newCache
    })
  }, [enabled, saveCache])

  return {
    getCachedMessages,
    setCachedMessages,
    addMessageToCache
  }
}